import {Button, Form, Image, Input, message, Spin, Typography} from "antd";
import {Callbacks} from "rc-field-form/lib/interface";
import React, {useEffect, useState} from "react";
import Logo from "../assets/logo.jpg";
import Api from "../common/Api";
import {AxiosError} from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import variables from "../common/variables";

interface Props {
    spinning: boolean;
    onFinish: any;
    onFinishFailed: Callbacks['onFinishFailed'];
    setChallenge: React.Dispatch<React.SetStateAction<"FORGOT_PASSWORD" | "SIGN-IN" | "NEW_PASSWORD_REQUIRED">>;
    codeSent: boolean;
    recaptchaRef: any;
}

const {Title} = Typography;

const ForgotPasswordForm = ({spinning, onFinish, onFinishFailed, codeSent, recaptchaRef, setChallenge}: Props) => {
    const [form] = Form.useForm();
    const [state, setState] = useState("SEND_CODE")

    useEffect(() => {
        if (codeSent)
            setState("UPDATE_PASSWORD")
    }, [codeSent, setChallenge])

    return (
        <Spin tip="Loading..." spinning={spinning}>
            <Form
                form={form}
                style={{height: "100%"}}
                name="basic"
                labelAlign={"left"}
                wrapperCol={{
                    sm: {span: 15, offset: 4},
                    md: {span: 10, offset: 7},
                    lg: {span: 8, offset: 8},
                    xl: {span: 6, offset: 9},
                }}
                onFinish={(values) => onFinish(state, values)}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
            >
                <Form.Item>
                    <Title level={4} type="secondary" style={{textAlign: "center"}}>ACT Tracker Forgot Password
                        page</Title>
                    <Image
                        preview={false}
                        width={"100%"}
                        src={Logo}
                    />
                </Form.Item>
                <Form.Item
                    name="email"
                    hasFeedback
                    rules={[{required: true, type: 'email', message: 'Invalid email address!'}]}
                >
                    <Input type={"email"} disabled={codeSent} placeholder="Email"/>
                </Form.Item>

                {codeSent && (<><Form.Item
                    name="code"
                    hasFeedback
                    rules={[{required: true, message: 'Missing code!'}]}
                >
                    <Input type={"text"} placeholder="Code"/>
                </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[{required: true, message: 'Please input your password!'}]}
                        hasFeedback
                    >
                        <Input.Password placeholder="New password"/>
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                            {
                                required: true,
                                message: 'Please confirm your password!',
                            },
                            ({getFieldValue}) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The two passwords that you entered do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirm new password"/>
                    </Form.Item></>)}

                <Form.Item name="captcha" hasFeedback rules={[{required: true, message: 'Complete captcha!'}]}>
                    <ReCAPTCHA
                        onChange={(ev) => form.setFieldsValue({captcha: ev})}
                        sitekey={variables.captchaKey!}
                        ref={el => recaptchaRef.current[1] = el}
                    />
                </Form.Item>

                <Form.Item wrapperCol={{
                    sm: {span: 15, offset: 4},
                    md: {span: 10, offset: 7},
                    lg: {span: 8, offset: 8},
                    xl: {span: 6, offset: 9},
                }}>
                    <Button style={{width: "100%"}} type="primary" htmlType="submit">
                        {!codeSent ? "Send code" : "Change password"}
                    </Button>
                </Form.Item>
                <Form.Item wrapperCol={{
                    sm: {span: 15, offset: 4},
                    md: {span: 10, offset: 7},
                    lg: {span: 8, offset: 8},
                    xl: {span: 6, offset: 9},
                }}>
                    <Button style={{width: "100%"}} type="link" onClick={() => setChallenge("SIGN-IN")}>
                        Sign In
                    </Button>
                </Form.Item>
                {codeSent && <Form.Item wrapperCol={{
                    sm: {span: 15, offset: 4},
                    md: {span: 10, offset: 7},
                    lg: {span: 8, offset: 8},
                    xl: {span: 6, offset: 9},
                }}>
                    <Button style={{width: "100%"}} type="link" onClick={async () => {
                        try {
                            await Api.management.forgotPasswordSendCode(form.getFieldValue('email'));
                            message.success('Code sent!')
                        } catch (e) {
                            if (e instanceof AxiosError) {
                                const error = e as AxiosError;
                                return message.error(error.response?.data as string);
                            }
                            console.error(e)
                            message.error('Unknown error')
                        }
                    }}>
                        Resend code ?
                    </Button>
                </Form.Item>}
            </Form>
        </Spin>

    )
}

export default ForgotPasswordForm;
